import { ReactNode, useEffect, useMemo, useState } from "react";
import { useLocation, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Button,
  Checkbox,
  ConfigProvider,
  Drawer,
  Empty,
  Flex,
  Input,
  InputNumber,
  Modal,
  Popover,
  Spin,
  Table,
  TableProps,
  Typography,
} from "antd";
import {
  ArrowLeftOutlined,
  CalendarOutlined,
  CheckOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  PercentageOutlined,
  PlusOutlined,
  TeamOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import PageHeading from "components/PageHeading.component";
import WarningBox from "components/WarningBox";
import PATHS from "shared/constants/paths";
import { translate, translateWithVars } from "shared/languages";
import { th, tnum } from "shared/components/TableText";
import { colors } from "shared/theme/color";
import {
  commissionStructureLabel,
  formatCurrency,
  formatDeduction,
  formatSignedCurrency,
  getBookingStatusLabel,
  GOODWILL_BOOKING_LABEL,
  INACTIVE_REASON_LABELS,
  INACTIVE_REASON_TONE,
} from "shared/utils";
import { clientShortNames } from "shared/utils/clientNames";
import { bookingStatusTone } from "components/bookings/bookingStatusTone";
import { sectionLabelStyle } from "components/NavSectionLabel.component";
import { localTzDate, tzDate } from "shared/utils/dayjs";
import { saveBlobAsFile } from "shared/utils/download";
import { modalButtonsTheme, modalSubtitleStyle } from "components/modals/ModalChrome";
import { showConfirmModal } from "components/modals/confirmModal";
import { showDestructiveConfirm } from "components/modals/destructiveConfirm";
import MetricCard from "components/MetricCard.component";
import { ClassTypeBadge, PayrollRunStatusBadge, StatusBadge } from "pages/schedule/component/ScheduleStatusBadge";
import { useAuth } from "context/AuthContextProvider";
import { ClassCategory, CommissionMode, CommissionRateStructure, PayrollFrequency, PayrollItemKind, PayrollItemSource, PayrollRunStatus, UserType, karyawanTypeOf } from "shared/types/enum";
import { InactiveReason, PayrollLine, PayrollSessionClient, Payslip } from "shared/types";
import {
  payrollRecurringItemsOptions,
  payrollRunOptions,
  payrollRunsOptions,
  payrollSettingsOptions,
  payrollStaffOptions,
} from "hooks/queries/payroll";
import { getPayrollPayslip, getPayrollRunPayslips } from "hooks/api/payroll";
import {
  useAddPayrollLineItem,
  useDeletePayrollLineItem,
  useDeletePayrollRun,
  useFinalizePayrollRun,
  useRecomputePayrollRun,
  useReopenPayrollRun,
  useUpdatePayrollLineItem,
  useUpdatePayrollLineSalary,
} from "hooks/mutations/payroll";
import { currencyInputProps } from "./currencyInput";
import { loadReportPdf } from "shared/utils/loadPdfChunk";
import { textPx } from "shared/constants/zoom";

// Same small local map as index.tsx/PayrollHistoryPage.tsx (not shared —
// three lines each, not worth a cross-file import for).
const getFrequencyLabels = (): Record<PayrollFrequency, string> => ({
  [PayrollFrequency.Weekly]: translate("payrollPage.settings.frequency_weekly"),
  [PayrollFrequency.TwiceMonthly]: translate("payrollPage.settings.frequency_twice_monthly"),
  [PayrollFrequency.Monthly]: translate("payrollPage.settings.frequency_monthly"),
});

const sanitizeForFilename = (value: string) => value.replace(/[^a-zA-Z0-9]+/g, "_");

// A line's employee type. Sessions and commission only ever belong to an
// instructor, so a Staf line shows "-" in those columns instead of zeros
// that read like data. A server that does not send roles yet is treated
// as all-instructor: the worst case is today's look, never hidden numbers.
const lineType = (line: { staff: { user: { roles?: string[] } } }) =>
  line.staff.user.roles ? karyawanTypeOf(line.staff.user.roles) : UserType.Instructor;
const isStafLine = (line: { staff: { user: { roles?: string[] } } }) => lineType(line) === UserType.Staff;
// Instruktur first, then Staf, A to Z inside each group (same order as the
// Gaji Pokok table), so commission earners sit together at the top.
const orderLines = <L extends { staff: { user: { full_name: string; roles?: string[] } } }>(lines: L[]): L[] =>
  [...lines].sort(
    (a, b) => (isStafLine(a) ? 1 : 0) - (isStafLine(b) ? 1 : 0) || a.staff.user.full_name.localeCompare(b.staff.user.full_name),
  );

// A session counts only when someone was taught: attendee_count is the
// Completed + No Show + Refunded bookings at generation (server
// commission.ts), the same Sesi Selesai rule Performa Instruktur uses, so
// the two pages count the same classes. A class nobody booked, or whose
// bookings are still unmarked, stays in the drawer's breakdown at Rp 0 but
// is not a session here.
const isCountedSession = (s: { attendee_count: number }) => s.attendee_count > 0;
const unmarkedOf = (s: { unmarked_count?: number }) => s.unmarked_count ?? 0;
const unmarkedSessionsOf = (sessions: { unmarked_count?: number }[]) => sessions.filter((s) => unmarkedOf(s) > 0).length;
const unmarkedNote = (n: number) =>
  n === 1 ? translate("payrollPage.run.unmarked_note_one") : translateWithVars("payrollPage.run.unmarked_note", { n });

// A run's period is shorter than its cadence's full period when a frequency
// or closing-day change cut it, or an earlier run already paid part of it.
// Gaji Pokok and recurring items are copied per run and never prorated, so
// the manager is told to look before finalizing. Days are Jakarta days.
const fullPeriodDays = (run: { frequency: string; period_start: Date; period_end: Date }): number | null => {
  const start = tzDate(run.period_start);
  switch (run.frequency) {
    case PayrollFrequency.Weekly:
      return 7;
    case PayrollFrequency.Monthly:
      return start.daysInMonth();
    case PayrollFrequency.TwiceMonthly:
      return start.date() <= 15 ? 15 : start.daysInMonth() - 15;
    default:
      return null;
  }
};
const periodDays = (run: { period_start: Date; period_end: Date }) =>
  tzDate(run.period_end).startOf("day").diff(tzDate(run.period_start).startOf("day"), "day") + 1;

// Which sentence a boundary run needs, if any. Since 2026-10-13 every leftover
// stands on its own, so a new run is never LONGER than its cadence's period;
// the long branch stays for runs generated before that, when a 1 to 3 day
// leftover was joined into the week beside it (a Reopened one still shows
// the note, and Gaji Pokok there is still one period's worth).
const periodLengthNote = (run: { frequency: string; period_start: Date; period_end: Date }) => {
  const full = fullPeriodDays(run);
  const days = periodDays(run);
  if (full === null || days === full) return null;
  return {
    days,
    key: days < full ? "payrollPage.run.short_period_note" : "payrollPage.run.long_period_note",
  };
};

const payslipFilename = (payslip: Payslip) =>
  // Both bounds: around a frequency change a studio can hold two short runs
  // for one person in the same month, and the start alone could collide.
  `Slip_Gaji_${sanitizeForFilename(payslip.staff.user.full_name)}_${tzDate(payslip.run.period_start).format("YYYY-MM-DD")}_${tzDate(payslip.run.period_end).format("YYYY-MM-DD")}.pdf`;

const downloadPayslipPdf = async (payslip: Payslip, studio: string) => {
  const { renderReportPdf } = await loadReportPdf();
  const blob = await renderReportPdf({ slug: "slip-gaji", studio, data: payslip });
  saveBlobAsFile(blob, payslipFilename(payslip));
};

const SalaryEditForm = ({ line, runId, onDone }: { line: PayrollLine; runId: string; onDone: () => void }) => {
  const [amount, setAmount] = useState<number>(line.basic_salary);
  const { mutate, isPending } = useUpdatePayrollLineSalary();
  return (
    <Flex vertical gap={8} style={{ width: 220 }}>
      <InputNumber {...currencyInputProps} value={amount} onChange={(v) => setAmount((v as number) ?? 0)} />
      <ConfigProvider theme={modalButtonsTheme}>
        <Button
          size="small"
          type="primary"
          loading={isPending}
          onClick={() => mutate({ runId, lineId: line.id, amount }, { onSuccess: onDone })}
        >
          {translate("payrollPage.run.salary_save")}
        </Button>
      </ConfigProvider>
    </Flex>
  );
};

// Replaces the old single-item "+ Tambah Item" modal (Label + Jenis radio +
// Amount, one item per open/close) with a Rezerv-style panel: a running
// total, every existing item as its own row with a delete action, and an
// inline add-row at the bottom that stays open so multiple items can be
// added in one sitting instead of reopening per item.
//
// No Jenis (Tunjangan/Potongan) picker — but unlike an earlier version of
// this panel, that's NOT done via a signed amount either. A negative
// "Lain-lain" entry read as a deduction hiding under the Gaji (Earnings)
// group header, which already double-counts against the existing, separate
// Total Potongan column. Instead this panel is scoped to exactly ONE kind
// per instance via the `kind` prop — one instance opened from the Lain-lain
// column (always Allowance, plain positive amount) and a second opened from
// Total Potongan (always Deduction) — so "which column you clicked" is what
// decides Tunjangan vs Potongan, not a sign or a radio button. Every add
// still goes through the existing useAddPayrollLineItem mutation, so the
// underlying data/schema is unchanged.
const LineItemsPanel = ({
  line,
  runId,
  editable,
  kind,
  open,
  onClose,
}: {
  line?: PayrollLine;
  runId: string;
  editable: boolean;
  kind: PayrollItemKind;
  open: boolean;
  onClose: () => void;
}) => {
  const { mutate: deleteItem } = useDeletePayrollLineItem();
  const { mutateAsync: addItemAsync, isPending: isAdding } = useAddPayrollLineItem();
  const { mutateAsync: updateItemAsync, isPending: isUpdating } = useUpdatePayrollLineItem();
  const [draftLabel, setDraftLabel] = useState("");
  const [draftAmount, setDraftAmount] = useState<number | null>(null);
  // Which existing item is being edited in place, if any — a second local
  // draft, separate from the add-row's, so editing one item doesn't clobber
  // whatever's half-typed in the add row below it.
  const [editingItemId, setEditingItemId] = useState<string | undefined>();
  const [editLabel, setEditLabel] = useState("");
  const [editAmount, setEditAmount] = useState<number | null>(null);

  if (!line) return null;
  const items = line.items.filter((item) => item.kind === kind);
  const total = items.reduce((sum, item) => sum + item.amount, 0);
  const canAdd = draftLabel.trim().length > 0 && draftAmount != null && draftAmount > 0;
  const canSaveEdit = editLabel.trim().length > 0 && editAmount != null && editAmount > 0;
  const isDeduction = kind === PayrollItemKind.Deduction;

  // try/catch here (not just await) — mutateAsync rejects on failure even
  // though onError above already toasts it, and an unhandled rejection from
  // an onClick handler is the same class of bug that crashed ClassCommission
  // Page's doSave() earlier (an oversized amount rejected server-side with
  // nothing catching the throw). Swallow it; the toast already told the user.
  const handleAdd = async () => {
    if (!canAdd || draftAmount == null) return;
    try {
      await addItemAsync({ runId, lineId: line.id, label: draftLabel.trim(), kind, amount: draftAmount });
      setDraftLabel("");
      setDraftAmount(null);
    } catch {
      // onError already toasted.
    }
  };

  const startEdit = (item: PayrollLine["items"][number]) => {
    setEditingItemId(item.id);
    setEditLabel(item.label);
    setEditAmount(item.amount);
  };

  const handleSaveEdit = async () => {
    if (!canSaveEdit || editAmount == null || !editingItemId) return;
    try {
      await updateItemAsync({ runId, lineId: line.id, itemId: editingItemId, label: editLabel.trim(), kind, amount: editAmount });
      setEditingItemId(undefined);
    } catch {
      // onError already toasted; stay in edit mode so the user can retry.
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={420}
      title={`${translate(isDeduction ? "payrollPage.run.deduction_panel_title" : "payrollPage.run.misc_panel_title")} ${line.staff.user.full_name}`}
    >
      <Flex vertical gap={16}>
        <Flex justify="space-between" align="center" style={{ padding: "10px 14px", background: colors.bgSoft, borderRadius: 10 }}>
          <Typography.Text strong style={{ fontSize: textPx(13) }}>
            {translate(isDeduction ? "payrollPage.run.deduction_total_label" : "payrollPage.run.misc_total_label")}
          </Typography.Text>
          <Typography.Text strong style={tnum}>
            {isDeduction ? formatDeduction(total) : formatCurrency(total)}
          </Typography.Text>
        </Flex>

        <Flex vertical gap={10}>
          {items.map((item) =>
            editingItemId === item.id ? (
              <Flex key={item.id} align="center" gap={8}>
                <Input
                  value={editLabel}
                  onChange={(e) => setEditLabel(e.target.value)}
                  style={{ flex: 1 }}
                  autoFocus
                />
                {/* Fixed width, not a flex share, matching the add row's
                    amount field below — keeps both rows' boxes the same
                    size regardless of button count. */}
                <InputNumber
                  {...currencyInputProps}
                  value={editAmount ?? undefined}
                  onChange={(v) => setEditAmount(v as number)}
                  style={{ width: 120, flex: "none" }}
                />
                <ConfigProvider theme={modalButtonsTheme}>
                  <Button type="primary" size="small" icon={<CheckOutlined />} loading={isUpdating} disabled={!canSaveEdit} onClick={handleSaveEdit} />
                </ConfigProvider>
              </Flex>
            ) : (
              <Flex key={item.id} align="center" gap={8}>
                {/* Clicking the label or amount enters edit mode directly —
                    no separate pencil icon needed, matching the same
                    click-to-reveal pattern the run table's own rows use for
                    the detail drawer. Delete stays a dedicated icon since a
                    destructive action shouldn't share a click target with
                    "start editing". */}
                <Typography.Text
                  style={{ flex: 1, fontSize: textPx(13), cursor: editable ? "pointer" : "default" }}
                  onClick={() => editable && startEdit(item)}
                >
                  {item.label}
                </Typography.Text>
                <span
                  style={{ ...tnum, width: 120, textAlign: "right", cursor: editable ? "pointer" : "default" }}
                  onClick={() => editable && startEdit(item)}
                >
                  {isDeduction ? formatDeduction(item.amount) : formatCurrency(item.amount)}
                </span>
                {editable && (
                  <Button
                    size="small"
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() =>
                      showConfirmModal({
                        tone: "amber",
                        title: translate("payrollPage.run.delete_item_confirm_title"),
                        content: translate("payrollPage.run.delete_item_confirm_content"),
                        confirmLabel: translate("payrollPage.rates.row_delete"),
                        destructive: true,
                        onConfirm: () => deleteItem({ runId, lineId: line.id, itemId: item.id }),
                      })
                    }
                  />
                )}
              </Flex>
            ),
          )}
          {items.length === 0 && (
            <Typography.Text type="secondary" style={{ fontSize: textPx(13) }}>
              -
            </Typography.Text>
          )}
        </Flex>

        {editable && (
          <Flex align="center" gap={8}>
            <Input
              placeholder={translate("payrollPage.recurring.label_field")}
              value={draftLabel}
              onChange={(e) => setDraftLabel(e.target.value)}
              style={{ flex: 1 }}
            />
            <InputNumber
              {...currencyInputProps}
              placeholder={translate("payrollPage.recurring.amount_label")}
              value={draftAmount ?? undefined}
              onChange={(v) => setDraftAmount(v as number)}
              style={{ width: 120, flex: "none" }}
            />
            <ConfigProvider theme={modalButtonsTheme}>
              {/* size="small" matches the edit row's check button — without
                  it this defaults to AntD's larger "middle" size, which eats
                  more of the row's width and left the Label/Jumlah boxes
                  above visibly narrower than the edit row's. */}
              <Button type="primary" size="small" icon={<PlusOutlined />} loading={isAdding} disabled={!canAdd} onClick={handleAdd} />
            </ConfigProvider>
          </Flex>
        )}
      </Flex>
    </Drawer>
  );
};

// Checkbox-select which instructors' slips to grab, then one button fires
// them all off as separate downloads (still individual PDFs — a merged
// single-document export is a different, bigger feature, not what this is).
// A short stagger between each trigger is deliberate: firing several browser
// downloads back-to-back from one click is exactly what makes Chrome (and
// most browsers) show its "this site is trying to download multiple files"
// permission prompt — spacing them out doesn't remove that prompt (it can
// still appear once), but it keeps the downloads from all queuing at the
// same instant, which reads as more intentional/less like a glitch. Each
// download is wrapped so one failure doesn't stop the rest of the batch.
const DOWNLOAD_STAGGER_MS = 250;
const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const DownloadAllModal = ({ open, runId, studio, onClose }: { open: boolean; runId?: string; studio: string; onClose: () => void }) => {
  const { data: payslips, isLoading } = useQuery({
    queryKey: ["payroll", "runs", runId, "payslips", "modal"],
    queryFn: () => getPayrollRunPayslips(runId!),
    enabled: open && !!runId,
  });
  // Defaults to everyone selected — "Unduh Semua Slip" is the entry point,
  // so starting fully checked matches what the button's own label promises;
  // deselecting specific instructors is the exception, not the norm.
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isDownloading, setIsDownloading] = useState(false);
  useEffect(() => {
    if (payslips) setSelectedIds(payslips.map((p) => p.id));
  }, [payslips]);

  const orderedPayslips = useMemo(() => (payslips ? orderLines(payslips) : []), [payslips]);
  const allSelected = !!payslips && payslips.length > 0 && selectedIds.length === payslips.length;
  const someSelected = selectedIds.length > 0 && !allSelected;

  const toggleAll = (checked: boolean) => setSelectedIds(checked ? (payslips ?? []).map((p) => p.id) : []);
  const toggleOne = (id: string, checked: boolean) =>
    setSelectedIds((prev) => (checked ? [...prev, id] : prev.filter((existingId) => existingId !== id)));

  const handleDownloadSelected = async () => {
    const selected = (payslips ?? []).filter((p) => selectedIds.includes(p.id));
    setIsDownloading(true);
    try {
      for (let index = 0; index < selected.length; index++) {
        if (index > 0) await wait(DOWNLOAD_STAGGER_MS);
        try {
          await downloadPayslipPdf(selected[index], studio);
        } catch {
          // Swallow: keep going through the rest of the batch rather than
          // letting one instructor's failed render abort everyone else's.
        }
      }
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Modal open={open} onCancel={onClose} title={translate("payrollPage.run.download_all")} footer={null} destroyOnClose>
      {isLoading ? (
        <Spin />
      ) : (
        <Flex vertical gap={16}>
          <Flex vertical gap={2}>
            <Checkbox
              checked={allSelected}
              indeterminate={someSelected}
              onChange={(e) => toggleAll(e.target.checked)}
              style={{ paddingBlock: 6, borderBottom: `1px solid ${colors.line}`, marginBottom: 6 }}
            >
              <Typography.Text strong>{translate("payrollPage.run.select_all")}</Typography.Text>
            </Checkbox>
            {orderedPayslips.map((payslip) => (
              <Checkbox
                key={payslip.id}
                checked={selectedIds.includes(payslip.id)}
                onChange={(e) => toggleOne(payslip.id, e.target.checked)}
                style={{ paddingBlock: 4 }}
              >
                {payslip.staff.user.full_name}
              </Checkbox>
            ))}
          </Flex>
          <ConfigProvider theme={modalButtonsTheme}>
            <Button type="primary" block loading={isDownloading} disabled={selectedIds.length === 0} onClick={handleDownloadSelected}>
              {translate("payrollPage.run.download_selected_button")} ({selectedIds.length})
            </Button>
          </ConfigProvider>
        </Flex>
      )}
    </Modal>
  );
};

// A drawer rather than an inline expanded table row — an instructor can
// easily have 100+ sessions in a month (confirmed against real production
// data on the Performa Instruktur analytics page), and pushing that many
// rows inline would blow out the table's own layout, especially with
// several instructors' rows open at once. A drawer has its own scroll
// region, so it holds any number of sessions without disturbing the rest of
// the page, and only ever shows one instructor at a time.
//
// Session/commission breakdown only — no Item Tambahan list here. Viewing
// and deleting individual allowance/deduction items already lives in
// LineItemsPanel (opened from the Lain-lain/Total Potongan columns, each
// filtered to its own kind), so repeating that list here would just be a
// second, redundant place to do the same thing; keeping this drawer to one
// job (which classes earned what) makes it a cleaner "why is this number
// what it is" lookup.
type SessionRow = PayrollLine["sessions"][number];
const sessionTotal = (s: SessionRow) => s.commission_per_class + s.commission_per_attendee + s.commission_per_revenue;
// Who a private session was for, in place of the count: everyone booked on
// it, marked or not (calc.clients), so the lines under the names
// (komplimenter, belum ditandai) read as "of these people", and a session
// whose only client is still unmarked keeps its name. One client in full,
// two or three by first name ("Gita, Hana"; "Ayu L., Ayu P." when two share
// one, like the Jadwal calendar), and from four on the first three plus how
// many more ("Eka, Fajar, Indah +2"). Null when nobody is booked.
const MAX_NAMED_CLIENTS = 3;
const sessionClientsLine = (clients: PayrollSessionClient[]): string | null => {
  if (clients.length === 0) return null;
  if (clients.length === 1) return clients[0].name;
  const short = clientShortNames(
    clients.map((c) => c.name),
    clients.map((c) => c.first_name?.trim() || c.name.trim().split(/\s+/)[0]),
  );
  const named = short.slice(0, MAX_NAMED_CLIENTS).join(", ");
  const more = clients.length - MAX_NAMED_CLIENTS;
  return more > 0 ? `${named} +${more}` : named;
};
const isRevenueStructure = (structure: string | null | undefined) =>
  structure === CommissionRateStructure.RevenuePercent ||
  structure === CommissionRateStructure.RevenueTiered ||
  structure === CommissionRateStructure.RevenuePercentTiered;
// A revenue band is a Rupiah range ("Rp 100.000–Rp 299.999"), an attendee
// band a plain count ("1–2"), the way the class commission page labels them.
const tierRange = (tier: { min_value: number; max_value: number | null }, revenue: boolean) => {
  const fmt = (n: number) => (revenue ? formatCurrency(n) : String(n));
  return tier.max_value === null
    ? translateWithVars("payrollPage.run.calc.tier_open", { min: fmt(tier.min_value) })
    : `${fmt(tier.min_value)}–${fmt(tier.max_value)}`;
};
const percentOf = (x100: number) => (x100 / 100).toLocaleString("id-ID", { maximumFractionDigits: 2 });

// One labelled line of the breakdown: label on the left, value on the right,
// an optional muted explanation under the value.
const CalcRow = ({ label, value, detail, last }: { label: string; value: ReactNode; detail?: ReactNode; last?: boolean }) => (
  <Flex justify="space-between" gap={16} style={{ padding: "10px 0", borderBottom: last ? undefined : `1px solid ${colors.line}` }}>
    <Typography.Text type="secondary" style={{ fontSize: textPx(13), flexShrink: 0 }}>
      {label}
    </Typography.Text>
    <Flex vertical align="flex-end" style={{ textAlign: "right", minWidth: 0 }}>
      <span style={{ ...tnum, fontWeight: 500 }}>{value}</span>
      {detail && (
        <Typography.Text type="secondary" style={{ fontSize: textPx(12) }}>
          {detail}
        </Typography.Text>
      )}
    </Flex>
  </Flex>
);

// The rate line and the calculation line for a session, from its calc
// snapshot. Returns nulls where the structure or the rate was missing, so
// the pop-up can say so instead of showing a formula that multiplies by
// nothing.
const describeCalc = (s: SessionRow) => {
  const c = s.calc;
  if (!c || !c.mode || !c.rate_structure) return { rate: null, formula: null, unconfigured: "structure" as const };
  if (!c.rate_configured) return { rate: null, formula: null, unconfigured: "rate" as const };
  const amount = c.rate_amount;
  const range = c.tier ? tierRange(c.tier, isRevenueStructure(c.rate_structure)) : null;
  const t = (k: string, v: Record<string, unknown>) => translateWithVars(`payrollPage.run.calc.${k}`, v);
  // No attendee, no commission, whatever the structure (commission.ts on the
  // server applies the same rule): the rate line still says what the class
  // pays, the calculation line says why this session paid nothing.
  const described = describeConfiguredCalc(c, s.attendee_count, amount, range, t);
  if (s.attendee_count === 0) {
    return { ...described, formula: translate("payrollPage.run.calc.formula_empty") };
  }
  return described;
};

const describeConfiguredCalc = (
  c: NonNullable<SessionRow["calc"]>,
  attendeeCount: number,
  amount: number | null,
  range: string | null,
  t: (k: string, v: Record<string, unknown>) => string,
): { rate: string | null; formula: string | null; unconfigured: "structure" | null } => {
  if (c.mode === CommissionMode.PerClass) {
    return {
      rate: t("rate_per_class", { amount: formatCurrency(amount ?? 0) }),
      formula: t("formula_per_class", { amount: formatCurrency(amount ?? 0) }),
      unconfigured: null,
    };
  }
  switch (c.rate_structure) {
    case CommissionRateStructure.Flat:
      return {
        rate: t("rate_per_attendee", { amount: formatCurrency(amount ?? 0) }),
        formula: t("formula_per_attendee", { amount: formatCurrency(amount ?? 0), n: attendeeCount }),
        unconfigured: null,
      };
    case CommissionRateStructure.Tiered:
      return range
        ? { rate: t("rate_tier", { range, amount: formatCurrency(amount ?? 0) }), formula: t("formula_tier", { n: attendeeCount, range }), unconfigured: null }
        : { rate: translate("payrollPage.run.calc.tier_none"), formula: null, unconfigured: null };
    case CommissionRateStructure.RevenuePercent:
      return {
        rate: t("rate_percent", { percent: percentOf(amount ?? 0) }),
        formula: t("formula_percent", { revenue: formatCurrency(c.revenue), percent: percentOf(amount ?? 0) }),
        unconfigured: null,
      };
    case CommissionRateStructure.RevenueTiered:
      return range
        ? { rate: t("rate_tier", { range, amount: formatCurrency(amount ?? 0) }), formula: t("formula_revenue_tier", { revenue: formatCurrency(c.revenue), range }), unconfigured: null }
        : { rate: translate("payrollPage.run.calc.tier_none"), formula: null, unconfigured: null };
    case CommissionRateStructure.RevenuePercentTiered:
      return range
        ? {
            rate: t("rate_tier_percent", { range, percent: percentOf(amount ?? 0) }),
            formula: t("formula_revenue_tier_percent", { revenue: formatCurrency(c.revenue), range, percent: percentOf(amount ?? 0) }),
            unconfigured: null,
          }
        : { rate: translate("payrollPage.run.calc.tier_none"), formula: null, unconfigured: null };
    default:
      return { rate: null, formula: null, unconfigured: "structure" as const };
  }
};

// Everyone the counts above are made of, A to Z, each with the booking status
// it had at generation; a comp carries Voided and a goodwill credit Bonus,
// the chips Detail Jadwal and Detail Sesi use. Scrolls inside a fixed height
// so a full open class doesn't stretch the pop-up past the screen: the cap
// cuts the seventh row in half, which is what says there is more. Every row
// keeps its height (flexShrink 0) rather than being squeezed by the cap.
const SessionClientList = ({ clients }: { clients: PayrollSessionClient[] }) => (
  <div style={{ marginTop: 16 }}>
    <div style={{ ...sectionLabelStyle, marginBottom: 4 }}>{translate("payrollPage.run.calc.clients")}</div>
    <div style={{ maxHeight: 254, overflowY: "auto", overscrollBehavior: "contain", display: "flex", flexDirection: "column" }}>
      {clients.map((client, i) => (
        <Flex
          key={`${client.name}-${i}`}
          justify="space-between"
          align="center"
          gap={12}
          style={{ flexShrink: 0, padding: "8px 0", borderBottom: i === clients.length - 1 ? undefined : `1px solid ${colors.line}` }}
        >
          <span style={{ minWidth: 0, overflowWrap: "anywhere", fontSize: textPx(14) }}>{client.name}</span>
          <Flex align="center" gap={6} style={{ flexShrink: 0 }}>
            {client.comp && (
              <StatusBadge
                tone={INACTIVE_REASON_TONE[InactiveReason.MANUALLY_MARKED_INVALID]}
                label={INACTIVE_REASON_LABELS[InactiveReason.MANUALLY_MARKED_INVALID]}
              />
            )}
            {client.goodwill && <StatusBadge tone="neutral" label={GOODWILL_BOOKING_LABEL} />}
            <StatusBadge tone={bookingStatusTone(client.status)} label={getBookingStatusLabel(client.status)} />
            {/* A class that took more than one credit says how many (absent
                on runs stored before multi-credit classes). */}
            {client.credit_cost !== undefined && client.credit_cost !== 1 && (
              <Typography.Text type="secondary" style={{ ...tnum, fontSize: textPx(12), whiteSpace: "nowrap" }}>
                {translateWithVars("customer_package.credit_cost_each", { n: client.credit_cost })}
              </Typography.Text>
            )}
          </Flex>
        </Flex>
      ))}
    </div>
  </div>
);

// "Why is this number what it is", for one session: the structure the class
// had, who was counted (and who was not, and why), the revenue when the
// structure reads it, the rate or the band that applied, and the one-line
// calculation that produced the commission. Everything comes from the
// row's calc snapshot, so it describes the rule as it stood at generation.
const SessionCalcModal = ({ session, onClose }: { session: SessionRow | null; onClose: () => void }) => {
  const navigate = useNavigate();
  if (!session) return null;
  const c = session.calc;
  const d = describeCalc(session);
  const revenueBased = isRevenueStructure(c?.rate_structure);
  const v = (k: string, vars: Record<string, unknown>) => translateWithVars(`payrollPage.run.calc.${k}`, vars);
  return (
    <Modal open onCancel={onClose} footer={null} width={520} title={translate("payrollPage.run.calc.title")}>
      <Flex justify="space-between" align="flex-start" gap={12} style={{ marginBottom: 12 }}>
        <Flex vertical gap={4} style={{ minWidth: 0 }}>
          <Flex align="center" gap={8}>
            <Typography.Text strong style={{ fontSize: textPx(15) }}>
              {session.class_type_name}
            </Typography.Text>
            <ClassTypeBadge type={session.category} />
          </Flex>
          <Typography.Text type="secondary" style={{ fontSize: textPx(13) }}>
            {localTzDate(session.occurrence_timestamp).format("dddd, DD MMM YYYY · HH:mm")}
          </Typography.Text>
        </Flex>
        {/* The class's Detail Jadwal, to check who is booked on it now. The
            drawer and this pop-up ride the run page's URL, so Back from there
            returns here. */}
        <Button
          type="link"
          size="small"
          icon={<CalendarOutlined />}
          style={{ flexShrink: 0, paddingInline: 0 }}
          onClick={() =>
            navigate(
              `${PATHS.SCHEDULE}/${session.schedule_id}?occurrence_timestamp=${encodeURIComponent(
                new Date(session.occurrence_timestamp).toISOString(),
              )}`,
            )
          }
        >
          {translate("payrollPage.run.calc.view_schedule")}
        </Button>
      </Flex>
      {!c ? (
        <Typography.Paragraph type="secondary" style={{ fontSize: textPx(13) }}>
          {translate("payrollPage.run.calc.unavailable")}
        </Typography.Paragraph>
      ) : (
        <div style={{ background: colors.bgSoft, borderRadius: 8, padding: "2px 16px" }}>
          <CalcRow
            label={translate("payrollPage.run.calc.structure")}
            value={commissionStructureLabel(c.mode, c.rate_structure)}
            detail={d.unconfigured === "structure" ? translate("payrollPage.run.calc.structure_none") : undefined}
          />
          <CalcRow
            label={translate("payrollPage.run.calc.attendees")}
            value={session.attendee_count}
            detail={v("attendees_detail", c.counts)}
          />
          {c.counts.unmarked > 0 && (
            <CalcRow label={translate("payrollPage.run.calc.unmarked")} value={c.counts.unmarked} detail={v("unmarked_detail", { n: c.counts.unmarked })} />
          )}
          {c.counts.comp > 0 && (
            <CalcRow label={translate("payrollPage.run.calc.comp")} value={c.counts.comp} detail={v("comp_detail", { n: c.counts.comp })} />
          )}
          {(c.counts.goodwill ?? 0) > 0 && (
            <CalcRow label={translate("payrollPage.run.calc.goodwill")} value={c.counts.goodwill} detail={v("goodwill_detail", { n: c.counts.goodwill })} />
          )}
          {revenueBased && (
            <CalcRow label={translate("payrollPage.run.calc.revenue")} value={formatCurrency(c.revenue)} detail={translate("payrollPage.run.calc.revenue_detail")} />
          )}
          {d.unconfigured !== "structure" && (
            <CalcRow
              label={translate("payrollPage.run.calc.rate")}
              value={d.rate ?? "-"}
              detail={d.unconfigured === "rate" ? translate("payrollPage.run.calc.rate_none") : undefined}
            />
          )}
          {d.formula && <CalcRow label={translate("payrollPage.run.calc.formula")} value={d.formula} />}
          <CalcRow label={translate("payrollPage.run.calc.result")} value={<span style={{ fontSize: textPx(18), fontWeight: 600 }}>{formatCurrency(sessionTotal(session))}</span>} last />
        </div>
      )}
      {c?.clients && c.clients.length > 0 && <SessionClientList clients={c.clients} />}
    </Modal>
  );
};

const LineDetailDrawer = ({
  line,
  open,
  onClose,
  calcSessionId,
  onCalcSessionChange,
}: {
  line?: PayrollLine;
  open: boolean;
  onClose: () => void;
  calcSessionId?: string;
  onCalcSessionChange: (sessionId?: string) => void;
}) => {
  const calcSession = line?.sessions.find((s) => s.id === calcSessionId) ?? null;
  const sessionColumns: TableProps<SessionRow>["columns"] = [
    {
      title: th(translate("payrollPage.run.sessions_col_date")),
      dataIndex: "occurrence_timestamp",
      // Fits the widest date + time on one line ("28 May 2026 · 00:00").
      width: 164,
      // The start time tells apart two classes of one type on the same day.
      render: (v: Date) => <span style={{ ...tnum, whiteSpace: "nowrap" }}>{localTzDate(v).format("DD MMM YYYY · HH:mm")}</span>,
    },
    {
      title: th(translate("payrollPage.run.sessions_col_class")),
      dataIndex: "class_type_name",
      render: (name: string, s) => (
        <Flex vertical gap={2}>
          <span>{name}</span>
          <Typography.Text type="secondary" style={{ fontSize: textPx(12) }}>
            {commissionStructureLabel(s.calc?.mode ?? null, s.calc?.rate_structure ?? null)}
          </Typography.Text>
        </Flex>
      ),
    },
    {
      title: th(translate("payrollPage.run.sessions_col_attendees")),
      dataIndex: "attendee_count",
      width: 150,
      // The number (on a private session, who it was for instead: their names
      // tell two sessions of one class apart, and an open class's list is in
      // the pop-up), then one muted line per thing worth knowing about it:
      // attendees who came in on a voided sale (still counted and paid per
      // participant, but adding no revenue), attendees still waiting to be
      // marked (they earn nothing until they are, shown whatever the count
      // beside them), or genuinely nobody. An older server sends none of
      // these fields, so the cell then stays the bare number it has always
      // been, and so does a private row stored before the client list
      // existed. The payslip PDF prints the number only (no client names).
      render: (v: number, s: SessionRow) => {
        const comps = s.comp_count ?? 0;
        const unmarked = unmarkedOf(s);
        const clients = s.category !== ClassCategory.Open ? s.calc?.clients : undefined;
        const names = clients ? sessionClientsLine(clients) : null;
        return (
          <Flex vertical>
            {clients ? names && <span>{names}</span> : <span style={tnum}>{v}</span>}
            {comps > 0 && (
              <Typography.Text type="secondary" style={{ fontSize: textPx(12) }}>
                {translateWithVars("payrollPage.run.comp_suffix", { count: comps })}
              </Typography.Text>
            )}
            {unmarked > 0 && (
              <Typography.Text type="warning" style={{ fontSize: textPx(12) }}>
                {translateWithVars("payrollPage.run.session_unmarked", { n: unmarked })}
              </Typography.Text>
            )}
            {v === 0 && unmarked === 0 && (
              <Typography.Text type="secondary" style={{ fontSize: textPx(12) }}>
                {translate("payrollPage.run.session_no_attendees")}
              </Typography.Text>
            )}
          </Flex>
        );
      },
    },
    {
      title: th(translate("payrollPage.run.sessions_col_commission")),
      key: "commission",
      align: "right",
      width: 130,
      render: (_, s) => <span style={{ ...tnum, fontWeight: 500 }}>{formatCurrency(sessionTotal(s))}</span>,
    },
  ];

  if (!line) return null;
  const unmarkedSessions = unmarkedSessionsOf(line.sessions);

  return (
    <Drawer open={open} onClose={onClose} width={680} title={`${translate("payrollPage.run.detail_drawer_title")} ${line.staff.user.full_name}`}>
      {unmarkedSessions > 0 && (
        <Typography.Paragraph type="warning" style={{ fontSize: textPx(13) }}>
          {unmarkedNote(unmarkedSessions)}
        </Typography.Paragraph>
      )}
      {line.sessions.length === 0 ? (
        <Empty description={translate("payrollPage.run.sessions_empty")} image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <>
          <Table
            className="payroll-session-table"
            size="small"
            bordered
            rowKey="id"
            pagination={false}
            columns={sessionColumns}
            dataSource={line.sessions}
            rowClassName={(s) => (isCountedSession(s) ? "" : "payroll-session-uncounted")}
            onRow={(s) => ({ onClick: () => onCalcSessionChange(s.id), style: { cursor: "pointer" } })}
            summary={() => (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={3}>
                  <Typography.Text strong>Total</Typography.Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  <span style={{ ...tnum, fontWeight: 600 }}>{formatCurrency(line.sessions.reduce((sum, s) => sum + sessionTotal(s), 0))}</span>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            )}
          />
        </>
      )}
      <SessionCalcModal session={calcSession} onClose={() => onCalcSessionChange(undefined)} />
    </Drawer>
  );
};

const PayrollRunPage = () => {
  const { runId } = useParams<{ runId: string }>();
  const navigate = useNavigate();
  const { state: authState } = useAuth();
  const { data: run, isLoading } = useQuery(payrollRunOptions(runId));
  const { mutate: finalizeRun, isPending: isFinalizing } = useFinalizePayrollRun();
  const { mutate: deleteRun, isPending: isDeleting } = useDeletePayrollRun();
  const { mutate: reopenRun, isPending: isReopening } = useReopenPayrollRun();
  const { mutate: recomputeRun, isPending: isRecomputing } = useRecomputePayrollRun();
  // Which line's LineItemsPanel is open, and scoped to which kind — Lain-lain
  // always opens it with kind=Allowance, Total Potongan always opens it with
  // kind=Deduction; one shared piece of state covers both since only one can
  // be open at a time.
  const [itemsPanel, setItemsPanel] = useState<{ lineId: string; kind: PayrollItemKind } | undefined>();
  const [downloadAllOpen, setDownloadAllOpen] = useState(false);
  // Which line's session/item detail drawer is open (a row click, not an
  // inline expand: see LineDetailDrawer's own comment for why), and which
  // session's Rincian Komisi pop-up inside it. Both ride the URL (`line`,
  // `session`), written in place, so Back from the pop-up's Lihat Jadwal
  // reopens the same drawer and pop-up. location.state is carried along, or
  // the write would drop the `from` the page arrived with.
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const detailLineId = searchParams.get("line") ?? undefined;
  const calcSessionId = searchParams.get("session") ?? undefined;
  const showDetail = (lineId?: string, sessionId?: string) => {
    const params = new URLSearchParams(searchParams);
    if (lineId) params.set("line", lineId);
    else params.delete("line");
    if (lineId && sessionId) params.set("session", sessionId);
    else params.delete("session");
    setSearchParams(params, { replace: true, state: location.state });
  };
  // Which line's Gaji Pokok pop-up is open, so Simpan can close it.
  const [salaryLineId, setSalaryLineId] = useState<string | undefined>();
  // Above the early return below (hooks must run on every render).
  const orderedLines = useMemo(() => orderLines(run?.lines ?? []), [run]);
  // Drift note on a DRAFT: does any line differ from what a Hitung Ulang
  // would load from Pengaturan? Same rules as the server's refresh, so a
  // hand-edited salary (overridden) or an edited / removed recurring copy
  // never counts as drift. Departed staff included so every line resolves;
  // a line whose person is not on the roster (or an older server without
  // the flags) is treated as no drift.
  const isDraft = run?.status === PayrollRunStatus.Draft;
  const { data: rosterDefaults = [], isSuccess: rosterFetched } = useQuery({ ...payrollStaffOptions(true), enabled: isDraft });
  const { data: recurringItems = [], isSuccess: recurringFetched } = useQuery({ ...payrollRecurringItemsOptions(), enabled: isDraft });
  const settingsDrift = useMemo(() => {
    if (!run || run.status !== PayrollRunStatus.Draft) return false;
    // Not until both reads have SUCCEEDED: against an empty item list every
    // carried recurring copy looks like drift (the note flashed on every open,
    // and a failed read would show it for good).
    if (!rosterFetched || !recurringFetched) return false;
    const defaults = new Map(rosterDefaults.map((s) => [s.user_id, s.default_basic_salary ?? 0]));
    return run.lines.some((line) => {
      if (line.basic_salary_overridden === undefined || line.excluded_recurring_item_ids === undefined) return false;
      if (!line.basic_salary_overridden && defaults.has(line.staff_id) && line.basic_salary !== defaults.get(line.staff_id)) return true;
      const excluded = new Set(line.excluded_recurring_item_ids);
      const carried = new Set(
        line.items.filter((i) => i.source !== PayrollItemSource.Recurring).map((i) => i.recurring_item_id).filter((id): id is string => !!id),
      );
      const expected = recurringItems
        .filter((r) => r.staff_id === line.staff_id && !excluded.has(r.id) && !carried.has(r.id))
        .map((r) => `${r.id}|${r.label}|${r.kind}|${r.amount}`)
        .sort();
      const actual = line.items
        .filter((i) => i.source === PayrollItemSource.Recurring)
        .map((i) => `${i.recurring_item_id}|${i.label}|${i.kind}|${i.amount}`)
        .sort();
      return expected.join("\n") !== actual.join("\n");
    });
  }, [run, rosterDefaults, recurringItems, rosterFetched, recurringFetched]);

  // Gaji Pokok and recurring items are one amount per person with no
  // frequency attached, so around a frequency change the stored amount is
  // right for one cadence and wrong for the other. Two reads, both only
  // while the run can still be edited: the cadence in force now, and the
  // studio's other runs, to find the payroll before this one.
  const editableStatus = run?.status === PayrollRunStatus.Draft || run?.status === PayrollRunStatus.Reopened;
  const { data: settings } = useQuery({ ...payrollSettingsOptions(), enabled: editableStatus });
  const { data: allRuns = [] } = useQuery({ ...payrollRunsOptions(), enabled: editableStatus });

  if (isLoading || !run) {
    return (
      <Flex justify="center" style={{ padding: 60 }}>
        <Spin />
      </Flex>
    );
  }

  const editable = run.status === PayrollRunStatus.Draft || run.status === PayrollRunStatus.Reopened;
  // Non-null only when this run does not cover a full period of the cadence
  // that produced it: a boundary run, one an earlier run trimmed, or a week
  // that absorbed a short leftover.
  const lengthNote = periodLengthNote(run);
  const unmarkedSessions = editable ? run.lines.reduce((sum, l) => sum + unmarkedSessionsOf(l.sessions), 0) : 0;
  // The frequency check, the real money risk around a change: this run is
  // the odd one out either because it follows a cadence that is no longer
  // in force (a leftover old-frequency period), or because it is the FIRST
  // run on the new cadence and the one before it was still the old one. A
  // full-length run in either state copies an amount set for the other
  // period length, and the length note above cannot see that, since both
  // are normal lengths. A studio that never changed frequency never trips
  // either branch.
  const inForce = settings?.exists ? settings.frequency : undefined;
  const previousRun = allRuns
    .filter((r) => r.id !== run.id && new Date(r.period_start).getTime() < new Date(run.period_start).getTime())
    .sort((a, b) => new Date(b.period_start).getTime() - new Date(a.period_start).getTime())[0];
  const frequencyNote = !editable
    ? null
    : inForce && run.frequency !== inForce
      ? translateWithVars("payrollPage.run.frequency_mismatch_note", { frequency: getFrequencyLabels()[run.frequency] })
      : previousRun && previousRun.frequency !== run.frequency
        ? translateWithVars("payrollPage.run.frequency_first_note", {
            frequency: getFrequencyLabels()[run.frequency],
            previous: getFrequencyLabels()[previousRun.frequency],
          })
        : null;
  // Everything the manager should look at before finalizing, as one box:
  // each warning is a FACT (bold) and the ACTION it calls for (regular),
  // and two facts that share an action print that action once, so the box
  // never says "check the basic salary" twice in a row.
  const warnings: { fact: string; action: string }[] = [];
  if (settingsDrift) {
    warnings.push({ fact: translate("payrollPage.run.settings_drift_note"), action: translate("payrollPage.run.settings_drift_action") });
  }
  if (unmarkedSessions > 0) {
    warnings.push({ fact: unmarkedNote(unmarkedSessions), action: translate("payrollPage.run.unmarked_action") });
  }
  if (editable && lengthNote) {
    warnings.push({
      fact: translateWithVars(lengthNote.key, { days: lengthNote.days }),
      action: translate("payrollPage.run.check_salary_action"),
    });
  }
  if (frequencyNote) {
    warnings.push({ fact: frequencyNote, action: translate("payrollPage.run.check_salary_action") });
  }
  // The reason either note above matters, as its own bold line: the amounts
  // were copied as set, not scaled to this period or cadence.
  if ((editable && lengthNote) || frequencyNote) {
    warnings.push({ fact: translate("payrollPage.run.salary_not_adjusted_note"), action: translate("payrollPage.run.check_salary_action") });
  }
  const warningGroups = warnings.reduce<{ action: string; facts: string[] }[]>((groups, w) => {
    const group = groups.find((g) => g.action === w.action);
    if (group) group.facts.push(w.fact);
    else groups.push({ action: w.action, facts: [w.fact] });
    return groups;
  }, []);
  const studio = authState.business?.name ?? "";
  const totalNetPay = run.lines.reduce((sum, l) => sum + l.net_pay, 0);
  const totalCommission = run.lines.reduce(
    (sum, l) => sum + l.commission_per_class + l.commission_per_attendee + l.commission_per_revenue,
    0,
  );

  const handleDownloadOne = async (line: PayrollLine) => {
    const payslip = await getPayrollPayslip({ runId: run.id, lineId: line.id });
    await downloadPayslipPdf(payslip, studio);
  };

  // AntD automatically rowSpans every column that has no `children` across
  // both header rows once ANY column (Komisi below) introduces a second row
  // — by default that spanned cell centers its label vertically. Pinning it
  // to the bottom instead lines every column's own name up with "Per
  // Kelas"/"Per Peserta"/"Per Pendapatan" (the leaf row, right above the
  // data) rather than the "Komisi" group row — matching Rezerv's own
  // convention (their ungrouped "Staff name"/"Total class sales" labels sit
  // at the bottom of the header block too, not the top).
  const bottomAlignHeaderCell = () => ({ style: { verticalAlign: "bottom" as const } });

  // Row-1 tick marks bounding the "Gaji"/"Komisi" merged-header groups —
  // applied directly to the specific columns that sit on each boundary via
  // a className (see .payroll-tick-start/.payroll-tick-end in index.css),
  // rather than a position-based nth-child CSS selector. Position-based
  // selectors broke twice already from unrelated DOM structure changes (the
  // hidden expandable-toggle column being removed, then fixed-left/right
  // columns splitting the header into separate mini-tables) — tagging the
  // exact column directly is immune to any future reordering/structural
  // change, since it doesn't depend on which numeric slot the column lands
  // in or which of AntD's internal tables it renders into.
  const tickHeaderCell = (side: "start" | "end") => () => ({
    style: { verticalAlign: "bottom" as const },
    className: side === "start" ? "payroll-tick-start" : "payroll-tick-end",
  });

  const columns: TableProps<PayrollLine>["columns"] = [
    {
      // Pinned so identity stays visible while scrolling through the
      // detail columns — this table already needs horizontal scroll (the
      // commission breakdown alone is 3 columns), and losing track of
      // which row you're looking at defeats the point of the breakdown.
      title: th(translate("payrollPage.run.col_instructor")),
      dataIndex: ["staff", "user", "full_name"],
      onHeaderCell: bottomAlignHeaderCell,
      fixed: "left",
      // Name plus a muted type line (Instruktur / Staf): no badge, no extra
      // column, and the pinned column keeps the table as narrow as before.
      render: (v: string, line) => (
        <Flex vertical gap={0}>
          <Typography.Text style={{ fontWeight: 600 }}>{v}</Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: textPx(12), lineHeight: 1.3 }}>
            {isStafLine(line) && line.staff.job_title
              ? `${translate("karyawan_type.staff")} · ${line.staff.job_title}`
              : translate(`karyawan_type.${lineType(line)}`)}
          </Typography.Text>
        </Flex>
      ),
    },
    // "Penghasilan" groups Basic Salary with the Miscellaneous column
    // (Rezerv's own "Earnings" grouping) — same merged-header pattern as
    // Komisi below. Broader than "Gaji" (strictly salary/wage), which fits
    // better since Lain-lain can hold non-salary items too; also
    // deliberately not "Pendapatan", which would read as a duplicate of the
    // existing "Total Pendapatan" grand-total column further right.
    {
      // Tick on this group's own left edge (not Instruktur's right edge —
      // Instruktur is pinned/fixed-left now, rendered in a separate <table>
      // from this one, so there's no single cell whose edge sits exactly on
      // that seam). Since Penghasilan is the first column of the scrollable
      // middle table, its own left edge already sits exactly where
      // Instruktur's fixed column ends, so this reproduces the same visual
      // tick without needing to reach across two different tables.
      title: th(translate("payrollPage.run.col_earnings_group")),
      // Soft background on the group-label row only — a structural aid so
      // the header visibly reads as two tiers (group label on top, specific
      // columns below), not a second color accent. Deliberately muted
      // (bgSoft, not a saturated fill) so it doesn't compete with THP's
      // successTint, which is the one intentional point of color emphasis
      // in this table.
      onHeaderCell: () => ({ className: "payroll-tick-start", style: { background: colors.bgSoft } }),
      children: [
        {
          title: th(translate("payrollPage.run.col_basic_salary")),
          key: "basic_salary",
          render: (_, line) =>
            editable ? (
              <Popover
                trigger="click"
                open={salaryLineId === line.id}
                onOpenChange={(open) => setSalaryLineId(open ? line.id : undefined)}
                // Closes once Simpan has saved, and opens fresh each time (a
                // value typed but never saved is not shown again).
                destroyOnHidden
                // The Popover renders in a portal, but React still bubbles its
                // clicks through the component tree to the row, so a click on
                // the field or Simpan also opened the detail drawer.
                content={
                  <div onClick={(e) => e.stopPropagation()}>
                    <SalaryEditForm line={line} runId={run.id} onDone={() => setSalaryLineId(undefined)} />
                  </div>
                }
              >
                {/* Rows are clickable (opens the detail drawer) — stop this
                    click from bubbling to the row so it only opens the
                    Popover, not both. fontWeight:400 overrides the app's
                    global Button theme (fontWeight:600 on every button) —
                    without it this would render bold just from being a
                    Button, not because it's meant to stand out like Total
                    Pendapatan/Potongan/THP. */}
                <Button type="text" size="small" style={{ ...tnum, fontWeight: 400 }} onClick={(e) => e.stopPropagation()}>
                  {formatCurrency(line.basic_salary)}
                  <EditOutlined style={{ marginInlineStart: 6 }} />
                </Button>
              </Popover>
            ) : (
              <span style={tnum}>{formatCurrency(line.basic_salary)}</span>
            ),
        },
        {
          // Allowance-only total (Tunjangan) — grouped under "Penghasilan"
          // (Earnings), so it must never include deductions; Total Potongan
          // below is the parallel deduction-only column/panel. The edit icon
          // opens LineItemsPanel scoped to kind=Allowance; the full list
          // (and deleting individual entries, of either kind) still lives in
          // the detail drawer.
          title: th(translate("payrollPage.run.col_miscellaneous")),
          key: "miscellaneous",
          render: (_, line) => {
            const total = line.items.reduce((sum, item) => (item.kind === PayrollItemKind.Allowance ? sum + item.amount : sum), 0);
            return editable ? (
              <Button
                type="text"
                size="small"
                style={{ ...tnum, fontWeight: 400 }}
                onClick={(e) => {
                  e.stopPropagation();
                  setItemsPanel({ lineId: line.id, kind: PayrollItemKind.Allowance });
                }}
              >
                {formatCurrency(total)}
                <EditOutlined style={{ marginInlineStart: 6 }} />
              </Button>
            ) : (
              <span style={tnum}>{formatCurrency(total)}</span>
            );
          },
        },
      ],
    },
    // Split by category (matching the Performa Instruktur analytics page's
    // own Sesi Open/Sesi Private breakdown) rather than one combined "Sesi"
    // count — derived client-side from each session's own `category`
    // (snapshotted server-side at generation time, see
    // payroll_run_line_session.category), not a separate stored aggregate,
    // since the full session list is already fetched for the detail drawer.
    // Counted sessions only (isCountedSession), so the numbers here equal
    // Performa Instruktur's for the same instructor and period.
    {
      title: th(translate("payrollPage.run.col_sessions_open")),
      key: "sessions_open",
      onHeaderCell: tickHeaderCell("start"),
      render: (_, line) => (
        <span style={tnum}>{isStafLine(line) ? "-" : line.sessions.filter((s) => s.category === ClassCategory.Open && isCountedSession(s)).length}</span>
      ),
    },
    {
      title: th(translate("payrollPage.run.col_sessions_private")),
      key: "sessions_private",
      onHeaderCell: bottomAlignHeaderCell,
      render: (_, line) => (
        <span style={tnum}>{isStafLine(line) ? "-" : line.sessions.filter((s) => s.category === ClassCategory.Private && isCountedSession(s)).length}</span>
      ),
    },
    // Same reasoning as the Sesi split above, and for the same underlying
    // reason it matters more here: private classes typically run 1-3
    // attendees while open classes can run 10+, so one combined "Peserta"
    // total mixes two very different scales — 5 could mean "nearly full
    // private class" or "under-filled open class," and there's no way to
    // tell which from the number alone.
    {
      title: th(translate("payrollPage.run.col_attendees_open")),
      key: "attendees_open",
      onHeaderCell: bottomAlignHeaderCell,
      render: (_, line) => (
        <span style={tnum}>{isStafLine(line) ? "-" : line.sessions.filter((s) => s.category === ClassCategory.Open).reduce((sum, s) => sum + s.attendee_count, 0)}</span>
      ),
    },
    {
      title: th(translate("payrollPage.run.col_attendees_private")),
      key: "attendees_private",
      onHeaderCell: tickHeaderCell("end"),
      render: (_, line) => (
        <span style={tnum}>{isStafLine(line) ? "-" : line.sessions.filter((s) => s.category === ClassCategory.Private).reduce((sum, s) => sum + s.attendee_count, 0)}</span>
      ),
    },
    // Commission breakdown by structure, grouped under one merged "Komisi"
    // header row (Rezerv's own "Commissions earnings" grouping) — two header
    // rows total, no middle "Classes"/"Pricing Plan" row like Rezerv has,
    // since every one of our structures is class-based (we have no sales-
    // attribution sibling group to distinguish from). Previously invisible
    // altogether, silently folded into Total Pendapatan — Per Peserta and
    // Per Pendapatan are two separate backend buckets (commission_per_
    // attendee/commission_per_revenue) specifically so they can each get
    // their own accurate column here. Leaf labels match the exact structure
    // names from the Struktur picker (ClassCommissionPage.tsx) so a column
    // traces directly back to the structure an admin actually configured.
    {
      title: th(translate("payrollPage.run.col_commission_group")),
      onHeaderCell: () => ({ style: { background: colors.bgSoft } }),
      children: [
        { title: th(translate("payrollPage.run.col_commission_class")), dataIndex: "commission_per_class", render: (v: number, line) => <span style={tnum}>{isStafLine(line) ? "-" : formatCurrency(v)}</span> },
        { title: th(translate("payrollPage.run.col_commission_attendee")), dataIndex: "commission_per_attendee", render: (v: number, line) => <span style={tnum}>{isStafLine(line) ? "-" : formatCurrency(v)}</span> },
        { title: th(translate("payrollPage.run.col_commission_revenue")), dataIndex: "commission_per_revenue", render: (v: number, line) => <span style={tnum}>{isStafLine(line) ? "-" : formatCurrency(v)}</span> },
      ],
    },
    {
      // Bold alongside Total Potongan and THP (not the detail columns
      // before it) — these three are the actual math trail to the final
      // number (Total Pendapatan − Total Potongan = THP), so bolding all
      // three lets that arithmetic be traced at a glance, the way a P&L
      // bolds its subtotal/total rows while line items stay regular.
      title: th(translate("payrollPage.run.col_total_earnings")),
      dataIndex: "total_earnings",
      onHeaderCell: tickHeaderCell("start"),
      render: (v: number) => <span style={{ ...tnum, fontWeight: 600 }}>{formatCurrency(v)}</span>,
    },
    {
      // total_deductions is already server-computed (sums every Item
      // Tambahan with kind=Deduction), so the edit icon here is the same
      // "quick add" shortcut as Lain-lain, just scoped to the opposite kind
      // — clicking THIS column always creates a Potongan, never a Tunjangan.
      // Bold alongside Total Pendapatan and THP — see that column's comment.
      title: th(translate("payrollPage.run.col_total_deductions")),
      dataIndex: "total_deductions",
      onHeaderCell: bottomAlignHeaderCell,
      render: (v: number, line) =>
        editable ? (
          <Button
            type="text"
            size="small"
            style={{ ...tnum, fontWeight: 600 }}
            onClick={(e) => {
              e.stopPropagation();
              setItemsPanel({ lineId: line.id, kind: PayrollItemKind.Deduction });
            }}
          >
            {formatDeduction(v)}
            <EditOutlined style={{ marginInlineStart: 6 }} />
          </Button>
        ) : (
          <span style={{ ...tnum, fontWeight: 600 }}>{formatDeduction(v)}</span>
        ),
    },
    {
      // Pinned alongside Slip (see below) — the "final result and action"
      // pair stays reachable without scrolling all the way right every
      // time, same reasoning as pinning Instruktur on the left.
      title: th(translate("payrollPage.run.col_net_pay")),
      dataIndex: "net_pay",
      // Tinted, matching Rezerv's own highlighted "Total net pay" column —
      // this is the one number on the page an owner actually needs to walk
      // away with. Merges the tint with the same bottom-alignment every
      // other ungrouped column gets. THP has no group above it, so its <th>
      // spans the full two-row header height as one cell — a flat fill
      // would tint the blank row-1 space above "THP" too. A hard-stop
      // gradient instead leaves the top half transparent and only fills
      // from roughly where row 2 (the "THP" label itself) begins, matching
      // how Gaji/Komisi's own tint stays confined to just their own row.
      // The top half is the card colour, not transparent: THP is pinned, so
      // the scrolled columns' headers pass underneath it.
      onHeaderCell: () => ({
        style: { background: `linear-gradient(to bottom, ${colors.card} 50%, ${colors.successTint} 50%)`, verticalAlign: "bottom" as const },
      }),
      onCell: () => ({ style: { background: colors.successTint } }),
      fixed: "right",
      render: (v: number) => <span style={{ ...tnum, fontWeight: 600 }}>{formatSignedCurrency(v)}</span>,
    },
    {
      title: th(translate("payrollPage.run.col_payslip")),
      key: "payslip",
      onHeaderCell: bottomAlignHeaderCell,
      fixed: "right",
      render: (_, line) => (
        <Button
          size="small"
          icon={<DownloadOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            handleDownloadOne(line);
          }}
        />
      ),
    },
  ];

  return (
    <>
      <PageHeading
        title={
          <Flex align="center" gap={12}>
            <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate(PATHS.PAYROLL_HISTORY)}>
              {translate("payrollPage.run.back_to_history")}
            </Button>
            <PayrollRunStatusBadge status={run.status} />
          </Flex>
        }
        extra={
          <Flex gap={10}>
            <Button icon={<DownloadOutlined />} onClick={() => setDownloadAllOpen(true)}>
              {translate("payrollPage.run.download_all")}
            </Button>
            {/* Sessions are a snapshot taken when the run was generated (and
                again on reopen). A schedule created or changed since, e.g. a
                backdated class recorded into this period, is invisible to the
                run until it is recomputed. On a DRAFT the recompute also
                reloads basic salaries and recurring items from Pengaturan
                (hand edits on this page survive); on a Reopened run those are
                frozen, so the confirm copy differs by status. */}
            {/* A draft that was never finalized can be removed outright (a
                wrong closing date or staff pick used to have no way out but
                finalizing it); a run that was ever finalized is the record
                of what was paid and is reopened, never deleted. */}
            {run.status === PayrollRunStatus.Draft && (
              <Button
                danger
                loading={isDeleting}
                onClick={() =>
                  showDestructiveConfirm({
                    title: translate("payrollPage.run.delete_draft_confirm_title"),
                    message: translateWithVars("payrollPage.run.delete_draft_confirm_content", {
                      period: `${localTzDate(run.period_start).format("D MMM")} - ${localTzDate(run.period_end).format("D MMM YYYY")}`,
                    }),
                    confirmLabel: translate("payrollPage.run.delete_draft_button"),
                    onConfirm: () => deleteRun(run.id, { onSuccess: () => navigate(PATHS.PAYROLL_HISTORY) }),
                  })
                }
              >
                {translate("payrollPage.run.delete_draft_button")}
              </Button>
            )}
            {editable && (
              <Button
                loading={isRecomputing}
                onClick={() =>
                  showConfirmModal({
                    title: translate("payrollPage.run.recompute_confirm_title"),
                    content: translate(
                      run.status === PayrollRunStatus.Draft
                        ? "payrollPage.run.recompute_confirm_content_draft"
                        : "payrollPage.run.recompute_confirm_content",
                    ),
                    confirmLabel: translate("payrollPage.run.recompute_button"),
                    onConfirm: () => recomputeRun(run.id),
                  })
                }
              >
                {translate("payrollPage.run.recompute_button")}
              </Button>
            )}
            <ConfigProvider theme={modalButtonsTheme}>
              {editable ? (
                <Button
                  type="primary"
                  loading={isFinalizing}
                  onClick={() =>
                    showConfirmModal({
                      // The amber header the Reopen dialog uses: finalizing is
                      // the step that makes the run permanent.
                      tone: "amber",
                      title: translate("payrollPage.run.finalize_confirm_title"),
                      // Fact in bold (what finalizing makes permanent: the
                      // run and its list of people), action regular, the
                      // same shape as the warning boxes (chris, 2026-10-13).
                      content: (
                        <Flex vertical gap={2} style={{ marginTop: 10 }}>
                          {/* The two facts sit together (the subtitle style's
                              own top margin only on the first), the action a
                              little apart, as in the warning boxes. */}
                          <Typography.Text style={{ ...modalSubtitleStyle, fontWeight: 700, color: colors.ink }}>
                            {translate("payrollPage.run.finalize_confirm_fact")}
                          </Typography.Text>
                          <Typography.Text style={{ ...modalSubtitleStyle, marginTop: 0, fontWeight: 700, color: colors.ink }}>
                            {translate("payrollPage.run.finalize_confirm_fact_2")}
                          </Typography.Text>
                          <Typography.Text style={{ ...modalSubtitleStyle, marginTop: 4, fontWeight: 500 }}>
                            {translate("payrollPage.run.finalize_confirm_action")}
                          </Typography.Text>
                        </Flex>
                      ),
                      confirmLabel: translate("payrollPage.run.finalize_button"),
                      onConfirm: () => finalizeRun(run.id),
                    })
                  }
                >
                  {translate("payrollPage.run.finalize_button")}
                </Button>
              ) : (
                <Button
                  type="primary"
                  loading={isReopening}
                  onClick={() =>
                    showConfirmModal({
                      tone: "amber",
                      title: translate("payrollPage.run.reopen_confirm_title"),
                      content: translate("payrollPage.run.reopen_confirm_content"),
                      confirmLabel: translate("payrollPage.run.reopen_button"),
                      onConfirm: () => reopenRun(run.id),
                    })
                  }
                >
                  {translate("payrollPage.run.reopen_button")}
                </Button>
              )}
            </ConfigProvider>
          </Flex>
        }
      />
      <Flex vertical gap={20}>
        <Flex gap={24}>
          <Typography.Text style={{ fontSize: textPx(13) }}>
            <Typography.Text type="secondary">{translate("payrollPage.history.col_frequency")}: </Typography.Text>
            <Typography.Text strong>{getFrequencyLabels()[run.frequency]}</Typography.Text>
          </Typography.Text>
          <Typography.Text style={{ fontSize: textPx(13) }}>
            <Typography.Text type="secondary">{translate("payrollPage.history.col_period")}: </Typography.Text>
            <Typography.Text strong>
              {localTzDate(run.period_start).format("D MMM")} - {localTzDate(run.period_end).format("D MMM YYYY")}
            </Typography.Text>
          </Typography.Text>
        </Flex>
        <Flex gap={16}>
          <MetricCard icon={<TeamOutlined />} title={translate("payrollPage.run.kpi_staff_count")} value={run.lines.length} />
          <MetricCard icon={<WalletOutlined />} title={translate("payrollPage.run.kpi_net_pay")} value={formatSignedCurrency(totalNetPay)} />
          <MetricCard icon={<PercentageOutlined />} title={translate("payrollPage.run.kpi_commission")} value={formatCurrency(totalCommission)} />
        </Flex>
        {warningGroups.length > 0 && (
          // A finalized run is a record, not a warning, so the length and
          // frequency notes are only built while the run can be edited (the
          // drift and unmarked notes already are). Facts sharing an action go
          // in as one item, so the action is printed once.
          <WarningBox items={warningGroups.map((group) => ({ fact: group.facts, action: group.action }))} />
        )}

        {/* 16px either side of every cell (32px between columns), the same
            as the admin's other tables; `size="middle"` alone gives 8px,
            which crowded the 15 columns together. The table scrolls sideways
            when that doesn't fit, with Karyawan, THP and Slip pinned. */}
        <ConfigProvider theme={{ components: { Table: { cellPaddingInlineMD: 16 } } }}>
        <Table<PayrollLine>
          className="payroll-run-table"
          rowKey="id"
          size="middle"
          columns={columns}
          dataSource={orderedLines}
          pagination={false}
          // Three new commission columns push this past a comfortable fixed
          // width — scroll horizontally inside the table rather than
          // squishing every column or overflowing the page.
          scroll={{ x: "max-content" }}
          // A row click opens the session/item detail drawer (see
          // LineDetailDrawer) rather than an inline expand — the individual
          // edit/download buttons inside a row stop propagation so they
          // don't also trigger this.
          onRow={(line) => ({ onClick: () => showDetail(line.id), style: { cursor: "pointer" } })}
        />
        </ConfigProvider>
      </Flex>

      <LineDetailDrawer
        open={!!detailLineId}
        line={run.lines.find((l) => l.id === detailLineId)}
        onClose={() => showDetail(undefined)}
        calcSessionId={calcSessionId}
        onCalcSessionChange={(sessionId) => showDetail(detailLineId, sessionId)}
      />
      <LineItemsPanel
        open={!!itemsPanel}
        line={run.lines.find((l) => l.id === itemsPanel?.lineId)}
        runId={run.id}
        editable={editable}
        kind={itemsPanel?.kind ?? PayrollItemKind.Allowance}
        onClose={() => setItemsPanel(undefined)}
      />
      <DownloadAllModal open={downloadAllOpen} runId={run.id} studio={studio} onClose={() => setDownloadAllOpen(false)} />
    </>
  );
};

export default PayrollRunPage;
